/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { PATHS } from '../../router/paths';
import {
  Activity,
  AlertTriangle,
  RefreshCw,
  Building2,
  Settings,
  Languages,
  XOctagon
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import StatWidget from '@components/back-office/shared/stat-widget';
import { useGetAdminDashboard } from '@utils/api/admin/use-get-admin-dashboard';

/**
 * On-Prem Admin Dashboard
 * Displays system health, queue status, storage usage, and recent activity.
 * No billing or revenue metrics (on-prem specific).
 */
export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const { data, isLoading, isFetching, isError, dataUpdatedAt, refetch } =
    useGetAdminDashboard({ refetchInterval: 30000 });

  // API returns data directly without 'result' wrapper
  const dashboard = data;

  // Format average processing time
  const formatMinutes = (minutes: number): string => {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Format last updated time
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <div className="flex items-start justify-between mb-2">
        <PageHeader
          title={t('dashboard')}
          caption={t('high-level-overview-of-system-health-and-metrics')}
        />

        {/* Auto-refresh indicator */}
        <div className="flex items-center gap-2 text-xs mt-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 px-2 py-1 rounded-2xl transition-colors"
            style={{
              color: getColour('inactiveText'),
              backgroundColor: isFetching
                ? getColour('thirdLayer')
                : 'transparent'
            }}
            title={t('refresh')}
          >
            <RefreshCw
              className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
            />
            {lastUpdated && (
              <span style={{ color: getColour('inactiveText') }}>
                {lastUpdated}
              </span>
            )}
          </button>
        </div>
      </div>

      {isError && (
        <div className="mb-6 rounded-2xl border border-red-500/50 bg-red-500/5 dark:bg-red-500/10 p-4">
          <p className="text-sm text-red-500 dark:text-red-400">
            {t('an-error-occurred')}
          </p>
        </div>
      )}

      {/* Key Metrics - 3 columns for important info */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('operational-overview')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatWidget
            title={t('active')}
            value={dashboard?.jobs?.active ?? 0}
            subtitle={t('currently-running')}
            icon={<Activity className="h-5 w-5" />}
            variant="success"
            loading={isLoading}
            onClick={() =>
              navigate(PATHS().ADMIN_OPERATIONS + '?status=processing')
            }
          />
          <StatWidget
            title={t('stranded-sessions')}
            value={dashboard?.jobs?.stranded ?? 0}
            subtitle={t('need-attention')}
            icon={<AlertTriangle className="h-5 w-5" />}
            variant={
              (dashboard?.jobs?.stranded ?? 0) > 0 ? 'warning' : 'default'
            }
            loading={isLoading}
            onClick={() =>
              navigate(PATHS().ADMIN_OPERATIONS + '?status=stranded')
            }
          />
          <StatWidget
            title={t('failed-24h')}
            value={dashboard?.jobs?.failed_24h ?? 0}
            subtitle={t('last-24-hours')}
            icon={<XOctagon className="h-5 w-5" />}
            variant={
              (dashboard?.jobs?.failed_24h ?? 0) > 0 ? 'danger' : 'default'
            }
            loading={isLoading}
            onClick={() =>
              navigate(PATHS().ADMIN_OPERATIONS + '?status=failed')
            }
          />
        </div>
      </section>

      {/* Queue & Jobs Details - 6 columns */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('queue-status-breakdown')}
        </h2>
        <div
          className="rounded-2xl border p-4"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('depth')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.queue?.depth ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('processing')}
              </div>
              <div className="text-xl font-semibold text-green-500 dark:text-green-400">
                {dashboard?.queue?.processing ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('waiting')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.queue?.queued ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('failed')}
              </div>
              <div
                className={`text-xl font-semibold ${(dashboard?.queue?.failed ?? 0) > 0 ? 'text-red-500 dark:text-red-400' : ''}`}
                style={
                  (dashboard?.queue?.failed ?? 0) > 0
                    ? undefined
                    : { color: getColour('normalText') }
                }
              >
                {dashboard?.queue?.failed ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('unprocessable')}
              </div>
              <div
                className={`text-xl font-semibold ${(dashboard?.queue?.dead_letter ?? 0) > 0 ? 'text-amber-500 dark:text-amber-400' : ''}`}
                style={
                  (dashboard?.queue?.dead_letter ?? 0) > 0
                    ? undefined
                    : { color: getColour('normalText') }
                }
              >
                {dashboard?.queue?.dead_letter ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('avg-processing-time')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {formatMinutes(dashboard?.jobs?.avg_processing_minutes ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storage, Database & Organizations - 6 columns */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('system-overview')}
        </h2>
        <div
          className="rounded-2xl border p-4"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('storage')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.storage?.driver?.toUpperCase() ?? 'N/A'}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('total-containers')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.storage?.container_count ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline cursor-pointer hover:opacity-80 transition-opacity"
              style={{ backgroundColor: getColour('thirdLayer') }}
              onClick={() => navigate(PATHS().ADMIN_ORGANIZATIONS)}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('orgs')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.database?.total_organizations ?? 0}
                <span className="text-sm font-normal ml-1 text-green-500 dark:text-green-400">
                  ({dashboard?.database?.active_organizations ?? 0})
                </span>
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('total-users')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.database?.total_users ?? 0}
                <span className="text-sm font-normal ml-1 text-green-500 dark:text-green-400">
                  ({dashboard?.database?.active_users ?? 0})
                </span>
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('total-sessions')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.database?.total_sessions ?? 0}
              </div>
            </div>
            <div
              className="p-3 rounded-2xl border border-hairline"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <div
                className="text-xs uppercase tracking-wide mb-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('last-30-days')}
              </div>
              <div
                className="text-xl font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {dashboard?.database?.sessions_last_30d ?? 0}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section>
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('quick-actions')}
        </h2>
        <div
          className="rounded-2xl border p-4"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <p
            className="text-sm mb-4"
            style={{ color: getColour('normalText') }}
          >
            {t(
              'use-the-tabs-above-to-access-detailed-views-and-perform-specific-actions'
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(PATHS().ADMIN_ORGANIZATIONS)}
              className="px-4 py-2 rounded-2xl text-sm font-medium transition-colors hover:opacity-80"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText'),
                border: `1px solid ${getColour('border')}`
              }}
            >
              <Building2 className="inline h-4 w-4 mr-2" />
              {t('manage-organizations')}
            </button>
            <button
              onClick={() => navigate(PATHS().ADMIN_OPERATIONS)}
              className="px-4 py-2 rounded-2xl text-sm font-medium transition-colors hover:opacity-80"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText'),
                border: `1px solid ${getColour('border')}`
              }}
            >
              <Activity className="inline h-4 w-4 mr-2" />
              {t('operations')}
            </button>
            <button
              onClick={() => navigate(PATHS().ADMIN_LANGUAGES)}
              className="px-4 py-2 rounded-2xl text-sm font-medium transition-colors hover:opacity-80"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText'),
                border: `1px solid ${getColour('border')}`
              }}
            >
              <Languages className="inline h-4 w-4 mr-2" />
              {t('languages')}
            </button>
            <button
              onClick={() => navigate(PATHS().ADMIN_SETTINGS)}
              className="px-4 py-2 rounded-2xl text-sm font-medium transition-colors hover:opacity-80"
              style={{
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText'),
                border: `1px solid ${getColour('border')}`
              }}
            >
              <Settings className="inline h-4 w-4 mr-2" />
              {t('settings')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
